/**
 * @description
 * VSCodeLens configuration modal component.
 * Allows users to configure VSCodeLens API settings directly from the VSCodeLens page.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 9, 2025
 */

import { useState, useEffect } from 'react';
import { X, Save, AlertCircle, CheckCircle, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../services/api';

interface VSCodeLensConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfigured: () => void;
}

interface VSCodeLensSettings {
  apiUrl: string;
  apiKey: string;
}

/**
 * VSCodeLens configuration modal component
 */
export function VSCodeLensConfigModal({ isOpen, onClose, onConfigured }: VSCodeLensConfigModalProps) {
  const [settings, setSettings] = useState<VSCodeLensSettings>({
    apiUrl: '',
    apiKey: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [testMessage, setTestMessage] = useState('');

  // Load current settings when modal opens
  useEffect(() => {
    if (isOpen) {
      loadCurrentSettings();
    }
  }, [isOpen]);

  const loadCurrentSettings = async () => {
    try {
      setLoading(true);
      const allSettings = await api.getSettings();
      
      const vscodeSettings = allSettings.filter(s => s.category === 'vscode_lens');
      const apiUrlSetting = vscodeSettings.find(s => s.key === 'vscode_lens_api_url');
      const apiKeySetting = vscodeSettings.find(s => s.key === 'vscode_lens_api_key');

      setSettings({
        apiUrl: apiUrlSetting?.value || '',
        apiKey: apiKeySetting?.value || '',
      });
    } catch (error) {
      console.error('Error loading VSCodeLens settings:', error);
      toast.error('Failed to load current settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Update API URL
      if (settings.apiUrl) {
        await api.updateSetting('vscode_lens_api_url', settings.apiUrl, 'admin');
      }
      
      // Update API Key
      if (settings.apiKey) {
        await api.updateSetting('vscode_lens_api_key', settings.apiKey, 'admin');
      }

      toast.success('VSCodeLens settings saved successfully');
      onConfigured();
      onClose();
    } catch (error) {
      console.error('Error saving VSCodeLens settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!settings.apiUrl || !settings.apiKey) {
      setTestResult('error');
      setTestMessage('Please enter both API URL and API Key');
      return;
    }

    try {
      setTestResult('testing');
      setTestMessage('Testing connection...');

      // Temporarily update settings for testing
      await api.updateSetting('vscode_lens_api_url', settings.apiUrl, 'admin');
      await api.updateSetting('vscode_lens_api_key', settings.apiKey, 'admin');

      // Test the connection
      const response = await fetch('/api/vscode-lens/tables');
      const data = await response.json();

      if (response.ok) {
        setTestResult('success');
        setTestMessage('Connection successful! VSCodeLens is properly configured.');
      } else {
        setTestResult('error');
        setTestMessage(data.error || 'Connection failed');
      }
    } catch (error) {
      setTestResult('error');
      setTestMessage('Connection failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-lg border border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 p-8 shadow-xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
              <Settings className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Configure VSCodeLens</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Set up your VSCodeLens API connection
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <div className="space-y-6">
          {/* API URL */}
          <div>
            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">API URL</label>
            <input
              type="url"
              value={settings.apiUrl}
              onChange={(e) => setSettings(prev => ({ ...prev, apiUrl: e.target.value }))}
              placeholder="https://vscode-lens-production.example.com"
              className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              disabled={loading}
            />
          </div>

          {/* API Key */}
          <div>
            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">API Key</label>
            <input
              type="password"
              value={settings.apiKey}
              onChange={(e) => setSettings(prev => ({ ...prev, apiKey: e.target.value }))}
              placeholder="Enter your VSCodeLens API key"
              className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              disabled={loading}
            />
          </div>

          {/* Test Result */}
          {testResult !== 'idle' && (
            <div className={`rounded-md border p-4 ${
              testResult === 'success'
                ? 'border-green-300 bg-green-50 text-green-800 dark:bg-green-950/20 dark:text-green-200'
                : testResult === 'error'
                ? 'border-red-300 bg-red-50 text-red-800 dark:bg-red-950/20 dark:text-red-200'
                : 'border-gray-300 bg-gray-50 text-gray-800 dark:bg-gray-950/20 dark:text-gray-200'
            }`}>
              <div className="flex items-center gap-3">
                {testResult === 'success' && <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />}
                {testResult === 'error' && <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />}
                {testResult === 'testing' && <div className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />}
                <span className="font-semibold">{testMessage}</span>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-8 flex gap-4">
          <button
            onClick={handleTestConnection}
            disabled={loading || saving || testResult === 'testing'}
            className="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            Test Connection
          </button>
          <button
            onClick={handleSave}
            disabled={loading || saving || !settings.apiUrl || !settings.apiKey}
            className="flex-1 rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-gray-800 disabled:opacity-50 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-gray-200"
          >
            {saving ? (
              <div className="flex items-center justify-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Saving...
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2">
                <Save className="h-4 w-4" />
                Save Configuration
              </div>
            )}
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-6 rounded-md border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
          <p className="text-xs text-gray-600 dark:text-gray-400">
            <strong className="font-semibold">Need help?</strong> Contact your administrator to get the VSCodeLens API URL and key.
            The API URL should point to your VSCodeLens service endpoint.
          </p>
        </div>
      </div>
    </div>
  );
}
